/**
 * Where GitHub sends a person after installing the TDIA GitHub App (DEC-200, UI_SDD §R.15).
 *
 * Public, like /signup: a new customer arriving from the Marketplace is not signed in yet.
 * The `code` GitHub adds is spent by one POST, which proves the person can access the
 * installation, links it to their workspace (creating one if they have none) and signs
 * them in. Nothing happens on load other than that single request.
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { githubAppApi, type GitHubSetupResult } from '../api/githubApp'
import { ApiError } from '../api/client'
import { useAuth } from '../hooks/useAuth'
import { Button } from '../components/shared/Button'
import { Spinner } from '../components/shared/Spinner'
import { PageTitle } from '../components/shared/PageTitle'

type State =
  | { phase: 'working' }
  | { phase: 'done'; result: GitHubSetupResult }
  | { phase: 'failed'; message: string }

export default function GitHubSetupPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { refresh } = useAuth()
  const [state, setState] = useState<State>({ phase: 'working' })
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true
    const code = params.get('code')
    const installationId = params.get('installation_id')
    if (!code || !installationId) {
      setState({
        phase: 'failed',
        message:
          'GitHub did not send the details TDIA needs. Install the app again from GitHub, or ask an owner of your GitHub organization to install it.',
      })
      return
    }
    void (async () => {
      try {
        const result = await githubAppApi.setup(code, installationId)
        if (result.signed_in) await refresh()
        setState({ phase: 'done', result })
      } catch (err) {
        setState({
          phase: 'failed',
          message: err instanceof ApiError ? err.message : 'Something went wrong. Try installing again.',
        })
      }
    })()
  }, [params, refresh])

  return (
    <div className="min-h-screen bg-ground flex flex-col">
      <header className="h-12 border-b border-hairline bg-panel flex items-center px-6">
        <span className="text-[15px] font-semibold text-ink tracking-tight">TDIA</span>
      </header>
      <main className="flex-1 flex items-start justify-center pt-24 px-4">
        <div className="w-full max-w-md">
          {state.phase === 'working' && (
            <div className="flex items-center gap-3">
              <Spinner />
              <span className="text-[14px] text-muted">Connecting GitHub to TDIA...</span>
            </div>
          )}

          {state.phase === 'done' && (
            <div data-testid="github-setup-done">
              <PageTitle eyebrow="GitHub App" size="compact">
                {`${state.result.account_login ?? 'GitHub'} is connected`}
              </PageTitle>
              <p className="text-[14px] text-muted mt-1 mb-5">
                Connected to the workspace <span className="text-ink">{state.result.workspace_name}</span>. TDIA
                scans these repositories on every push and posts results on pull requests.
              </p>
              <div className="bg-panel border border-hairline rounded-md px-4 py-3 mb-6">
                {state.result.repository_selection === 'all' ? (
                  <p className="text-[13px] text-body m-0">All repositories of {state.result.account_login}.</p>
                ) : state.result.repositories.length === 0 ? (
                  <p className="text-[13px] text-muted m-0">No repositories selected yet. Choose them on GitHub.</p>
                ) : (
                  <ul className="m-0 p-0 list-none space-y-1">
                    {state.result.repositories.map((r) => (
                      <li key={r} className="text-[13px] text-body">
                        {r}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <Button onClick={() => navigate('/scans', { replace: true })}>Done</Button>
            </div>
          )}

          {state.phase === 'failed' && (
            <div data-testid="github-setup-failed">
              <PageTitle eyebrow="GitHub App" size="compact">
                GitHub could not be connected
              </PageTitle>
              <p className="text-[14px] text-muted mt-1 mb-5">{state.message}</p>
              <Button variant="secondary" onClick={() => navigate('/login')}>
                Go to sign in
              </Button>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
